/**
 * Anexica Widget — entry point.
 *
 * Закрытое состояние: иконка + бейдж непрочитанных. Полный UI чата
 * подгружается лениво через `import('./full')` при первом клике.
 *
 * Встраивание на сайт клиента:
 *
 *   <script src="https://cdn.anexica.com/widget.js"
 *           data-key="aw_xxxxxxxxxxxx"
 *           data-origin="https://api.anexica.com"
 *           defer></script>
 *
 * data-origin опционален: дефолт — https://api.anexica.com. В dev
 * локально передаём data-origin="http://localhost:8080".
 */

import { findWidgetScript } from './config';
import { isMobileViewport } from './detect';
import { createT, detectLocale } from './i18n';
import { collectVisitorContext } from './context';
import type { Creds, CachedTheme } from './persist';
import type { ProactiveConfig } from './api';

// ANX-051: localStorage-ключ, по которому виджет помнит «pro-active welcome
// уже показывался для этого widget_key на этом устройстве». Срабатывает
// один раз на устройство per site — выбор владельца «закрыл триггер → не
// показывать в этой сессии». В реальности «сессии» хватает persistent
// флага: visitor может явно нажать launcher если хочет открыть.
const PROACTIVE_DISMISSED_PREFIX = 'anexica.proactive.dismissed.';

function proactiveDismissedKey(widgetKey: string): string {
  return PROACTIVE_DISMISSED_PREFIX + widgetKey;
}

function isProactiveDismissed(widgetKey: string): boolean {
  try {
    return localStorage.getItem(proactiveDismissedKey(widgetKey)) === '1';
  } catch {
    return false;
  }
}

function markProactiveDismissed(widgetKey: string): void {
  try {
    localStorage.setItem(proactiveDismissedKey(widgetKey), '1');
  } catch {
    // приватный режим — окей, виджет переживёт повторный показ
  }
}

const SCRIPT_TAG = findWidgetScript();
const WIDGET_KEY = SCRIPT_TAG?.dataset.key;
const ORIGIN = SCRIPT_TAG?.dataset.origin ?? 'https://api.anexica.com';
const LOCALE = detectLocale({
  override: SCRIPT_TAG?.dataset.lang ?? null,
  navigatorLang: typeof navigator !== 'undefined' ? navigator.language : null,
});
const T = createT(LOCALE);

// Ключ persist'а v2 (см. persist.ts) — нужен в closed bundle, чтобы прочитать
// кэш темы без подгрузки full chunk'а.
const CREDS_STORAGE_KEY = 'anexica.creds.v2';

// ANX-064 — публичный JS API: anexica('set', {plan:'pro', userId:'42'}) шлёт
// кастомные атрибуты о пользователе. Аккумулируем и flush'им, когда есть
// visitor-токен. Если сайт вызвал anexica(...) до загрузки скрипта через
// stub-очередь — проигрываем её.
type AnexicaFn = ((cmd: string, payload?: unknown) => void) & { q?: unknown[][] };

let pendingAttributes: Record<string, unknown> = {};
let flushingAttributes = false;

function currentVisitorToken(): string | null {
  try {
    const raw = localStorage.getItem(CREDS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: string; expiresAt?: number };
    const now = Math.floor(Date.now() / 1000);
    if (!parsed.token || !parsed.expiresAt || parsed.expiresAt <= now) return null;
    return parsed.token;
  } catch {
    return null;
  }
}

async function flushAttributes(): Promise<void> {
  if (flushingAttributes) return;
  if (Object.keys(pendingAttributes).length === 0) return;
  const token = currentVisitorToken();
  if (!token) return; // токена ещё нет — flush'нем после init
  const batch = pendingAttributes;
  pendingAttributes = {};
  flushingAttributes = true;
  try {
    await fetch(`${ORIGIN}/api/v1/widget/visitor/attributes`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(batch),
    });
  } catch {
    // вернём в очередь на следующий flush (новые ключи приоритетнее)
    pendingAttributes = { ...batch, ...pendingAttributes };
  } finally {
    flushingAttributes = false;
  }
}

function handleAnexicaCommand(cmd: unknown, payload: unknown): void {
  if (cmd === 'set' && payload && typeof payload === 'object' && !Array.isArray(payload)) {
    Object.assign(pendingAttributes, payload as Record<string, unknown>);
    void flushAttributes();
  }
}

(function setupAnexicaApi(): void {
  const w = window as unknown as { anexica?: AnexicaFn };
  const existing = w.anexica;
  const fn = ((cmd: string, payload?: unknown) => handleAnexicaCommand(cmd, payload)) as AnexicaFn;
  fn.q = [];
  w.anexica = fn;
  if (existing && Array.isArray(existing.q)) {
    for (const args of existing.q) handleAnexicaCommand(args[0], args[1]);
  }
})();

// Bootstrap — в самом конце модуля (см. низ файла). initWidget() обращается к
// module-level const'ам (PRESENCE_INTERVAL_MS и др.), объявленным ниже по файлу;
// вызов отсюда падал бы в TDZ («Cannot access … before initialization»).

/**
 * shouldStartProactive — гард, опровергающий запуск pro-active таймера.
 * Возвращает false если: фича выключена, нет message, dismissed-флаг
 * установлен, или mobile-режим выключен на этом устройстве.
 * Чистый predicate — удобно тестировать.
 */
export function _shouldStartProactiveForTests(
  proactive: ProactiveConfig | null | undefined,
  widgetKey: string,
  mobile: boolean,
): boolean {
  return shouldStartProactive(proactive, widgetKey, mobile);
}

export function _setDismissedForTests(widgetKey: string, dismissed: boolean): void {
  try {
    if (dismissed) localStorage.setItem(proactiveDismissedKey(widgetKey), '1');
    else localStorage.removeItem(proactiveDismissedKey(widgetKey));
  } catch {
    // ignore
  }
}

function shouldStartProactive(
  proactive: ProactiveConfig | null | undefined,
  widgetKey: string,
  mobile: boolean,
): boolean {
  if (!proactive) return false;
  if (!proactive.message || proactive.message.trim() === '') return false;
  if (proactive.delaySec <= 0) return false;
  if (mobile && !proactive.mobileEnabled) return false;
  if (isProactiveDismissed(widgetKey)) return false;
  return true;
}

/**
 * sendHeartbeat — ANX-067: лёгкий fire-and-forget ping при каждой загрузке
 * widget.js. Backend фиксирует факт+домен установки (детект для онбординг-
 * чеклиста). Домен он берёт из Origin-заголовка, который браузер проставит
 * сам — в теле его не шлём. sendBeacon не блокирует выгрузку и игнорирует
 * ответ; fallback на keepalive-fetch для старых браузеров. Любая ошибка
 * молча проглатывается — пинг не должен влиять на работу виджета.
 */
export function sendHeartbeat(origin: string, widgetKey: string): void {
  try {
    const url = `${origin}/api/v1/widget/heartbeat`;
    const body = JSON.stringify({ widget_key: widgetKey });
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, body);
    } else {
      void fetch(url, { method: 'POST', body, keepalive: true });
    }
  } catch {
    // best-effort
  }
}

// ANX-105 follow-up — client-persistent anonymous visitor id для СЧЁТЧИКОВ.
// Отдельный от creds (CREDS_STORAGE_KEY): тот протухает с visitor-JWT за 24ч, а
// avid живёт постоянно — чтобы вернувшийся визитёр считался тем же. Используется
// ТОЛЬКО для счётчиков «кто на сайте» (token-less /widget/visit), не для auth.
const AVID_STORAGE_KEY = 'anexica_avid_v1';

export function getOrCreateAvid(): string {
  try {
    let id = localStorage.getItem(AVID_STORAGE_KEY);
    if (!id) {
      id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : 'a' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(AVID_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
}

/**
 * sendVisit — ANX-105 follow-up: token-less visit-beacon для счётчиков. Шлётся
 * СРАЗУ на загрузке widget.js (до /widget/init), поэтому ловит «отказников»,
 * уходящих за время init-round-trip (presence их теряла). Fire-and-forget.
 */
export function sendVisit(origin: string, widgetKey: string, avid: string): void {
  if (!avid) return;
  try {
    const url = `${origin}/api/v1/widget/visit`;
    const body = JSON.stringify({ widget_key: widgetKey, visitor_id: avid });
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, body);
    } else {
      void fetch(url, { method: 'POST', body });
    }
  } catch {
    // best-effort
  }
}

/**
 * pingPresence — ANX-069: лёгкий heartbeat «визитёр сейчас на сайте». Шлёт
 * текущий URL в /widget/presence под visitor-токеном (если он уже получен).
 * Пропускается, когда вкладка скрыта или токена ещё нет. Fire-and-forget.
 */
export function pingPresence(origin: string): void {
  try {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    const token = currentVisitorToken();
    if (!token) return;
    // ANX-104: через sendBeacon, токен в ТЕЛЕ (не в Authorization). Раньше был
    // fetch(keepalive:true) с Authorization+JSON → CORS-preflight, который в
    // Chromium рвался (net::ERR_ABORTED) — presence долетал у ~5% визитёров,
    // «На сайте» казался пустым. sendBeacon шлёт «простой» запрос (text/plain)
    // без preflight и надёжно доставляет даже при уходе со страницы — как
    // heartbeat (sendHeartbeat). Fallback — fetch без keepalive.
    const url = `${origin}/api/v1/widget/presence`;
    const body = JSON.stringify({ token, url: typeof location !== 'undefined' ? location.href : '' });
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, body);
    } else {
      void fetch(`${origin}/api/v1/widget/presence`, { method: 'POST', body });
    }
  } catch {
    // best-effort
  }
}

// PRESENCE_INTERVAL_MS — чуть меньше backend-TTL (75с), чтобы один пропущенный
// пинг не выронил визитёра из live-списка.
const PRESENCE_INTERVAL_MS = 45_000;

function initWidget(widgetKey: string, origin: string): void {
  // ANX-067: детект установки — пингуем сразу на загрузке, до любого UI.
  sendHeartbeat(origin, widgetKey);

  // ANX-105 follow-up: visit-beacon для счётчиков — token-less, сразу на
  // загрузке (до init), поэтому ловит «отказников». На load + возврат вкладки
  // (не по 45с-таймеру — счётчики «5мин/час/сегодня» по времени прихода).
  const avid = getOrCreateAvid();
  sendVisit(origin, widgetKey, avid);

  // ANX-069: live-presence. Первый пинг — как только появится токен (в
  // ветках init ниже вызовем повторно), далее по таймеру и при возврате на
  // вкладку. До получения токена pingPresence сам себя пропускает.
  pingPresence(origin);
  window.setInterval(() => pingPresence(origin), PRESENCE_INTERVAL_MS);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        pingPresence(origin);
        sendVisit(origin, widgetKey, avid);
      }
    }, { passive: true });
  }

  const host = document.createElement('div');
  host.id = 'anexica-widget-host';
  host.style.cssText = 'position:fixed;bottom:20px;right:20px;z-index:2147483647;';
  document.body.appendChild(host);

  // Mobile: на host-сайтах без правильного viewport-meta @media не сработает.
  // Поэтому ставим host-атрибут по JS-детекту screen.width; CSS-правила
  // в styles.ts гейтятся через :host([data-mobile]) (см. detect.ts).
  const syncMobileAttr = (): void => {
    if (isMobileViewport()) host.setAttribute('data-mobile', '1');
    else host.removeAttribute('data-mobile');
  };
  syncMobileAttr();
  window.addEventListener('resize', syncMobileAttr, { passive: true });
  window.addEventListener('orientationchange', syncMobileAttr, { passive: true });

  const shadow = host.attachShadow({ mode: 'closed' });

  // Минимальный launcher: класс и иконка применятся в full.tsx::applyLauncherStyle
  // после загрузки полного UI. Здесь — только базовая кнопка, чтобы closed
  // bundle оставался крошечным.
  const button = document.createElement('button');
  button.setAttribute('aria-label', T('aria.openChat'));
  button.style.cssText =
    'width:56px;height:56px;border-radius:50%;background:#1292EE;color:#fff;' +
    'border:none;cursor:pointer;box-shadow:0 8px 24px rgba(20,40,70,0.18);' +
    'display:flex;align-items:center;justify-content:center;line-height:1;' +
    'font-family:system-ui,-apple-system,sans-serif;';
  // SVG-иконка chat (lucide-style). Inline без JSX, чтобы не тащить Preact
  // в closed bundle. Та же визуально, что и IconChat в icons.tsx.
  button.innerHTML =
    '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>' +
    '</svg>';
  shadow.appendChild(button);

  // Применяем тему ДО первого клика, чтобы пользователь не видел синий
  // дефолт пока полный chunk не загрузится. Источники, в порядке приоритета:
  //   1. Кэш из localStorage (быстрее всего, ноль задержки).
  //   2. Background fetch /widget/init (если кэша нет) — обновляет inline
  //      когда ответ пришёл, обычно за 100-300мс.
  // Тут же подхватываем proactive-config (ANX-051) — нужен для setTimeout
  // ниже без второго /widget/init вызова.
  const cached = readCachedInitData();
  if (cached) {
    applyEarlyTheme(host, button, cached.theme);
  } else {
    void fetchInitData(origin, widgetKey).then((data) => {
      if (data) {
        applyEarlyTheme(host, button, data.theme);
        maybeStartProactiveTimer(data.proactive);
      }
      // ANX-064: токен теперь есть — отправляем накопленные attributes.
      void flushAttributes();
      // ANX-069: и первый presence-пинг (до этого токена не было).
      pingPresence(origin);
    });
  }
  // Если creds уже в кэше — токен есть сразу, flush'им что накопилось.
  if (cached) {
    void flushAttributes();
    pingPresence(origin);
  }

  let isMounted = false;
  let isMounting = false;
  // mountChat импортируется лениво и хранит ссылку на mount-функцию из full.tsx
  // вместе с инстансом store, чтобы повторные клики не запускали init заново.
  let toggleOpen: (() => void) | null = null;
  // Ссылка на store нужна pro-active таймеру, который может сработать ПОСЛЕ
  // первого mount'а (когда виджет открыли, но visitor его закрыл).
  let mountedStore: { setProactiveWelcome(t: string | null): void; open(): void; isOpen: { value: boolean } } | null = null;

  async function openWidget(opts?: { proactiveMessage?: string }): Promise<void> {
    if (toggleOpen) {
      // Виджет уже mounted. На proactive-режим достаточно set welcome
      // и явный open() (toggle не подходит — может закрыть, если уже открыт).
      if (opts?.proactiveMessage && mountedStore) {
        mountedStore.setProactiveWelcome(opts.proactiveMessage);
        if (!mountedStore.isOpen.value) mountedStore.open();
      } else {
        toggleOpen();
      }
      return;
    }
    if (isMounting) return;
    isMounting = true;
    button.disabled = true;
    try {
      const [full, persist, apiModule, storeModule] = await Promise.all([
        import('./full'),
        import('./persist'),
        import('./api'),
        import('./store'),
      ]);

      // Получаем (или восстанавливаем) visitor JWT.
      const existing = persist.loadCreds();
      let creds: Creds | null = null;
      let bannedAtInit = false;
      if (existing) {
        creds = existing;
      } else {
        const tempApi = apiModule.createApi({ origin, getToken: () => null });
        try {
          const initResp = await tempApi.init({ widgetKey });
          creds = {
            token: initResp.token,
            visitorId: initResp.visitorId,
            expiresAt: Math.floor(Date.now() / 1000) + initResp.expiresIn,
            theme: initResp.site.widgetSettings,
            contacts: initResp.visitor,
            emailTranscriptsEnabled: initResp.site.emailTranscriptsEnabled,
            poweredBy: initResp.site.poweredBy,
            proactive: initResp.site.proactive,
            officeHours: initResp.site.officeHours,
            prechat: initResp.site.prechat,
          };
          persist.saveCreds(creds);
          void flushAttributes();
        } catch (e) {
          // ANX-046: visitor забанен — открываем полный UI в banned-режиме.
          // creds остаётся null; full.tsx рендерит banned-screen без чата.
          if (e && typeof e === 'object' && (e as { code?: string }).code === 'banned') {
            bannedAtInit = true;
          } else {
            throw e;
          }
        }
      }

      const store = storeModule.createChatStore();
      if (bannedAtInit) store.setBanned(true);
      const api = apiModule.createApi({
        origin,
        getToken: () => persist.loadCreds()?.token ?? null,
      });

      full.mount(shadow, button, {
        origin, creds, api, store, t: T,
        ...(creds?.theme ? { theme: creds.theme } : {}),
      });

      // Cache toggle для последующих кликов.
      toggleOpen = () => {
        if (store.isOpen.value) store.close();
        else store.open();
      };
      mountedStore = store;

      isMounted = true;
      // Pro-active: до open() добавляем welcome, чтобы он сразу виден.
      if (opts?.proactiveMessage) store.setProactiveWelcome(opts.proactiveMessage);
      store.open();
    } catch (e) {
      console.error('[Anexica] init failed', e);
    } finally {
      isMounting = false;
      button.disabled = false;
    }
  }

  button.addEventListener('click', () => { void openWidget(); });
  void isMounted;

  // ANX-051: pro-active trigger. Запускаем setTimeout после того, как
  // proactive-config известен (из кэша или background fetch). Если фича
  // выключена / dismissed / mobile-mismatch — гард фильтрует.
  function maybeStartProactiveTimer(proactive: ProactiveConfig | null | undefined): void {
    if (!shouldStartProactive(proactive, widgetKey, isMobileViewport())) return;
    const cfg = proactive as ProactiveConfig;
    window.setTimeout(() => {
      // Перепроверка на момент срабатывания: visitor мог открыть виджет
      // вручную или сменить ориентацию на mobile.
      if (mountedStore?.isOpen.value) return;
      if (isMobileViewport() && !cfg.mobileEnabled) return;
      if (isProactiveDismissed(widgetKey)) return;
      markProactiveDismissed(widgetKey);
      void openWidget({ proactiveMessage: cfg.message });
    }, cfg.delaySec * 1000);
  }
  if (cached?.proactive) {
    maybeStartProactiveTimer(cached.proactive);
  }
  // На случай, если читаем кэш v1 (без proactive), но background fetch
  // выше всё равно отработает в else-ветке и попробует setTimeout.
}

// ---- early theme application (без зависимости от full chunk) ----

/**
 * applyEarlyTheme — наложение темы inline-style'ами на host и launcher,
 * пока full chunk ещё не загружен. После клика mount→applyTheme
 * переподтвердит то же самое через CSS-vars (с !important в :host правилах),
 * чтобы избежать flicker.
 */
function applyEarlyTheme(host: HTMLElement, button: HTMLButtonElement, theme: CachedTheme): void {
  if (theme.position === 'left') {
    host.style.right = 'auto';
    host.style.left = '20px';
  }
  if (theme.themeColor) {
    button.style.background = theme.themeColor;
  }
}

/**
 * CachedInitData — то, что closed bundle может прочитать из persistent creds
 * без подгрузки full chunk'а: тема + (опционально) pro-active config.
 */
interface CachedInitData {
  theme: CachedTheme;
  proactive: ProactiveConfig | null;
}

function readCachedInitData(): CachedInitData | null {
  try {
    const raw = localStorage.getItem(CREDS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      expiresAt?: number;
      theme?: CachedTheme;
      proactive?: ProactiveConfig | null;
    };
    const now = Math.floor(Date.now() / 1000);
    if (!parsed.expiresAt || parsed.expiresAt <= now) return null;
    if (!parsed.theme) return null;
    return { theme: parsed.theme, proactive: parsed.proactive ?? null };
  } catch {
    return null;
  }
}

/**
 * fetchInitData — лёгкий POST /widget/init без api.ts (он в full chunk'е).
 * Возвращает тему + proactive (или null при любой ошибке). Side-effect:
 * сохраняет полный creds в localStorage, чтобы повторное открытие из
 * click-handler'а подхватило без повторного init'а.
 */
async function fetchInitData(origin: string, widgetKey: string): Promise<CachedInitData | null> {
  try {
    const resp = await fetch(`${origin}/api/v1/widget/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ widget_key: widgetKey, ...collectVisitorContext() }),
    });
    if (!resp.ok) return null;
    const body = await resp.json() as {
      visitor_id: string;
      token: string;
      expires_in: number;
      site: {
        id: string;
        widget_settings: { theme_color?: string; position?: string; greeting?: string } | null;
        email_transcripts_enabled?: boolean;
        powered_by?: boolean;
        proactive?: { delay_sec?: number; message?: string; mobile_enabled?: boolean } | null;
        office_hours?: {
          timezone?: string;
          days?: Record<string, { start?: string; end?: string }> | null;
          offline_message?: string;
          collect_email?: boolean;
        } | null;
        prechat?: unknown;
      };
      visitor?: { id: string; name?: string | null; email?: string | null; phone?: string | null };
    };
    const ws = body.site.widget_settings ?? {};
    const theme: CachedTheme = {
      themeColor: ws.theme_color || '#1292EE',
      position: ws.position === 'left' ? 'left' : 'right',
      greeting: ws.greeting ?? '',
    };
    const proactive: ProactiveConfig | null = body.site.proactive
      ? {
          delaySec: typeof body.site.proactive.delay_sec === 'number'
            ? body.site.proactive.delay_sec : 60,
          message: typeof body.site.proactive.message === 'string'
            ? body.site.proactive.message : '',
          mobileEnabled: body.site.proactive.mobile_enabled === true,
        }
      : null;
    const contacts: { id: string; name?: string; email?: string; phone?: string } = {
      id: body.visitor?.id ?? body.visitor_id,
    };
    if (body.visitor?.name) contacts.name = body.visitor.name;
    if (body.visitor?.email) contacts.email = body.visitor.email;
    if (body.visitor?.phone) contacts.phone = body.visitor.phone;
    // ANX-061: office hours кэшируем как есть (days нормализует mount в full
    // chunk'е — closed bundle не тянет office-hours.ts ради размера).
    const officeHours = body.site.office_hours
      ? {
          timezone: typeof body.site.office_hours.timezone === 'string'
            && body.site.office_hours.timezone.length > 0
            ? body.site.office_hours.timezone : 'UTC',
          days: body.site.office_hours.days ?? {},
          offlineMessage: typeof body.site.office_hours.offline_message === 'string'
            ? body.site.office_hours.offline_message : '',
          collectEmail: body.site.office_hours.collect_email !== false,
        }
      : null;
    // Сохраняем сразу, чтобы click-handler не сделал второй init.
    try {
      localStorage.setItem(CREDS_STORAGE_KEY, JSON.stringify({
        token: body.token,
        visitorId: body.visitor_id,
        expiresAt: Math.floor(Date.now() / 1000) + body.expires_in,
        theme,
        contacts,
        emailTranscriptsEnabled: body.site.email_transcripts_enabled === true,
        poweredBy: body.site.powered_by !== false,
        proactive,
        officeHours,
        // ANX-072: pre-chat кэшируем сырым (normalizePrechat в full chunk'е
        // нормализует — closed bundle не тянет api.ts ради размера).
        prechat: body.site.prechat ?? null,
      }));
    } catch {
      // приватный режим — игнорируем
    }
    return { theme, proactive };
  } catch {
    return null;
  }
}

// --- Bootstrap (точка входа) ---
// Должен стоять ПОСЛЕ всех module-level объявлений: initWidget() синхронно
// читает const'ы (PRESENCE_INTERVAL_MS и пр.), объявленные выше по файлу, но
// инициализируемые только при доезде исполнения до них. Вызов из середины
// модуля → ReferenceError TDZ → виджет не монтируется (регресс ANX-069).
if (!WIDGET_KEY) {
  console.error('[Anexica] data-key attribute is required');
} else {
  initWidget(WIDGET_KEY, ORIGIN);
}
